import { getAlpha2Code, getName } from 'i18n-iso-countries';
import _ from 'lodash';
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  CountryCluster,
  Language,
  Market,
} from '../../common/CountryMarketConfig';
import { Dropdown } from '../../components/Dropdown';
import { InputGroup } from '../../components/InputGroup';
import { Loading } from '../../components/Loading';
import { Modal } from '../../components/Modal/Modal';
import { GTMBaseContentViewObject } from '../../hooks/GTMUtils';
import { getCustomers } from '../../hooks/queries/useCustomers';
import { useAuth } from '../../hooks/useAuth';
import { useCountryMarketHelpers } from '../../hooks/useCountryMarketHelpers';
import { useGTM } from '../../hooks/useGTM';
import { useLocalizationCookieHandler } from '../../hooks/useLocalizationCookieHandler';
import {
  DropdownOption,
  DropdownOptionsWithUndefined,
} from '../../lib/Dropdown/types';
import { noOp } from '../../lib/utils';
import { TrackingSchema } from '../../types';
import { useCustomerState } from '../Briefing/CustomerState';

export type Reason = 'empty' | 'invalid' | 'preferences';

export type CountryMarketSelectorProps = {
  reason?: string;
  isHidden?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
};

const initial = <T,>(availableProp: T[]): T | undefined =>
  availableProp.length === 1 ? availableProp[0] : undefined;

const select = <T,>(
  selection: DropdownOptionsWithUndefined,
  dispatch: Dispatch<SetStateAction<T | undefined>>,
): void => {
  if (!selection) return;

  selection = Array.isArray(selection) ? selection[0] : selection;

  dispatch(selection.value as T);
};

const translateCountry = (country: string, locale: string) => {
  const language = locale.split('_')[0];
  const alpha2Code = getAlpha2Code(country, 'en');

  if (!alpha2Code) {
    return country;
  }

  return (
    getName(alpha2Code, language, {
      select: 'official',
    }) ?? country
  );
};

export const CountryMarketSelector: FC<CountryMarketSelectorProps> = ({
  reason,
  isHidden = false,
  onConfirm = noOp,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { changeAppLanguage } = useLocalizationCookieHandler();

  const [{ user, customers }] = useAuth();
  const [, { setCustomerId }] = useCustomerState();

  const { findCountryMarketConfigsByString, redirectTargetByCountryAndMarket } =
    useCountryMarketHelpers();

  const [selectedCustomer, setSelectedCustomer] = useState<string>();
  const [selectedCountry, setSelectedCountry] = useState<CountryCluster>();
  const [selectedMarket, setSelectedMarket] = useState<Market>();
  const [selectedLanguage, setSelectedLanguage] = useState<Language>();
  const { updateDataLayer } = useGTM();

  const userMarkets = user?.countries_and_markets;
  const customerMarkets =
    customers?.[selectedCustomer ?? '']?.countries_and_markets;

  const countryMarketConfigList = useMemo(
    () =>
      findCountryMarketConfigsByString(
        Object.values(customerMarkets ?? userMarkets ?? {}),
      ),
    [customerMarkets, findCountryMarketConfigsByString, userMarkets],
  );

  const availableCustomers: DropdownOption[] = useMemo(
    () =>
      Object.values(customers || {}).map(
        ({ customer_name, erp_id }): DropdownOption => ({
          name: customer_name,
          value: erp_id,
        }),
      ),
    [customers],
  );

  const availableCountries: DropdownOption[] = useMemo(() => {
    if (!selectedCustomer && availableCustomers.length) return [];

    return _.entries(countryMarketConfigList).map(
      ([countryCluster, cmcList]): DropdownOption => {
        const { countries } = _.values(cmcList)[0];

        return {
          name: translateCountry(countryCluster, i18n.language),
          subtitle:
            countryCluster !== (countries ?? [countryCluster])?.[0]
              ? (countries ?? [countryCluster])
                  .map((country) => translateCountry(country, i18n.language))
                  .join(', ')
              : undefined,
          value: countryCluster,
        };
      },
    );
  }, [
    selectedCustomer,
    availableCustomers.length,
    countryMarketConfigList,
    i18n.language,
  ]);

  const availableMarkets: DropdownOption[] = useMemo(() => {
    if (!selectedCountry) return [];

    const countryMarketConfigs = countryMarketConfigList[selectedCountry];

    return Object.keys(countryMarketConfigs || {}).map((market) => ({
      name: market,
      value: market,
    }));
  }, [selectedCountry, countryMarketConfigList]);

  const availableLanguages: DropdownOption[] = useMemo(() => {
    if (!selectedCountry || !selectedMarket) return [];

    const countryMarketConfig =
      countryMarketConfigList[selectedCountry]?.[selectedMarket];

    return (
      countryMarketConfig?.languages.map((language) => ({
        name: t(`labels:languages:${language}`),
        value: language,
      })) || []
    );
  }, [selectedCountry, selectedMarket, t, countryMarketConfigList]);

  const initialCustomer = initial(availableCustomers);
  const initialCountry = initial(availableCountries);
  const initialMarket = initial(availableMarkets);
  const initialLanguage = initial(availableLanguages);

  const selectCustomer = useCallback(
    (selection: DropdownOptionsWithUndefined) => {
      setSelectedCountry(undefined);
      setSelectedMarket(undefined);
      setSelectedLanguage(undefined);

      select(selection, setSelectedCustomer);
    },
    [],
  );

  const selectCountry = useCallback(
    (selection: DropdownOptionsWithUndefined) => {
      setSelectedMarket(undefined);
      setSelectedLanguage(undefined);

      select(selection, setSelectedCountry);
    },
    [],
  );

  const selectMarket = useCallback(
    (selection: DropdownOptionsWithUndefined) => {
      setSelectedLanguage(undefined);

      select(selection, setSelectedMarket);
    },
    [],
  );

  const selectLanguage = useCallback(
    (selection: DropdownOptionsWithUndefined) => {
      select(selection, setSelectedLanguage);
    },
    [],
  );

  const isLoading = !availableCustomers.length;

  const hiddenCustomerSelector = availableCustomers.length < 2;

  const disabledCountrySelector = availableCountries.length < 2;

  const disabledMarketSelector = availableMarkets.length < 2;

  const disabledLanguageSelector = availableLanguages.length < 2;

  const disabledConfirmation =
    !selectedCountry || !selectedMarket || !selectedLanguage || isLoading;

  const onSelectionConfirm = useCallback(async () => {
    if (
      disabledConfirmation ||
      !selectedCountry ||
      !selectedMarket ||
      !selectedLanguage
    )
      return;

    selectedCustomer && dispatch(setCustomerId(selectedCustomer));

    if (user && !hiddenCustomerSelector && selectedCustomer) {
      const selectedCustomerFull =
        customers &&
        Object.values(customers).find(
          ({ erp_id }) => erp_id === selectedCustomer,
        );
      const userTrackingData: Record<string, unknown> = {
        userGUID: user.GUID,
        identStatus: user.azelis_employee ? 'employee' : 'customer',
        customerGUID: selectedCustomerFull?.GUID,
        customerName: selectedCustomerFull?.customer_name,
        access: true,
      };

      const userCustomers = await getCustomers(user);

      if (user.customers.length > 0 && userCustomers) {
        userTrackingData.assignedCustomerGUIDs = userCustomers.map(
          ({ GUID }) => GUID,
        );

        userTrackingData.customerName = userCustomers.map(
          ({ customer_name }) => customer_name,
        );
      }

      updateDataLayer({
        event: 'User Switch Customer',
        schema: TrackingSchema.user,
        data: userTrackingData,
      });
    }

    changeAppLanguage(selectedLanguage).then(() => {
      onConfirm();
      navigate(
        redirectTargetByCountryAndMarket(
          selectedCountry,
          selectedMarket,
          'start',
        ),
      );
    });
  }, [
    changeAppLanguage,
    customers,
    disabledConfirmation,
    dispatch,
    hiddenCustomerSelector,
    navigate,
    onConfirm,
    redirectTargetByCountryAndMarket,
    selectedCountry,
    selectedCustomer,
    selectedLanguage,
    selectedMarket,
    setCustomerId,
    updateDataLayer,
    user,
  ]);

  useEffect(() => {
    if (isHidden) return;

    updateDataLayer({
      ...GTMBaseContentViewObject,
      data: {
        category: 'User Area',
        type: 'Select Market/User',
        content: 'Select Market',
      },
    });
    // Needed because of unnessesary rerenders of the app
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidden]);

  const universalCustomerSuffix = hiddenCustomerSelector
    ? ''
    : ':universalCustomer';

  return (
    <div data-testid="CountryMarketSelector">
      <Modal
        heading={t([
          `components:countryMarketSelector:reason:${reason}${universalCustomerSuffix}:heading`,
          `components:countryMarketSelector:reason:${reason}:heading`,
        ])}
        isHidden={isHidden}
        isComplex={true}
        confirmText={t('components:countryMarketSelector:confirm')}
        onConfirm={onSelectionConfirm}
        isConfirmButtonDisabled={disabledConfirmation}
        isCancelHidden={!onCancel}
        cancelText={t('components:countryMarketSelector:cancel')}
        onCancel={onCancel}
      >
        <p className="mb-8 font-regular">
          {t([
            `components:countryMarketSelector:reason:${reason}${universalCustomerSuffix}:body`,
            `components:countryMarketSelector:reason:${reason}:body`,
          ])}
        </p>

        {isLoading ? (
          <Loading />
        ) : (
          <>
            <div
              className={`mb-6 text-left ${hiddenCustomerSelector && 'hidden'}`}
            >
              <InputGroup
                label={t('components:countryMarketSelector:customer')}
              >
                <Dropdown
                  options={availableCustomers}
                  onChange={selectCustomer}
                  initialSelection={initialCustomer}
                  triggerOnChangeForInitialSelection={true}
                  allowUnset={false}
                  data-testid="CustomerSelector"
                />
              </InputGroup>
            </div>
            <div className="mb-6 text-left">
              <InputGroup label={t('components:countryMarketSelector:country')}>
                <Dropdown
                  options={availableCountries}
                  onChange={selectCountry}
                  initialSelection={initialCountry}
                  disabled={disabledCountrySelector}
                  triggerOnChangeForInitialSelection={true}
                  allowUnset={false}
                  typeahead={true}
                  data-testid="CountrySelector"
                />
              </InputGroup>
            </div>
            <div className="mb-6 text-left">
              <InputGroup label={t('components:countryMarketSelector:market')}>
                <Dropdown
                  options={availableMarkets}
                  onChange={selectMarket}
                  initialSelection={initialMarket}
                  disabled={disabledMarketSelector}
                  triggerOnChangeForInitialSelection={true}
                  allowUnset={false}
                  data-testid="MarketSelector"
                />
              </InputGroup>
            </div>
            <div className="text-left">
              <InputGroup
                label={t('components:countryMarketSelector:preferredLanguage')}
              >
                <Dropdown
                  options={availableLanguages}
                  onChange={selectLanguage}
                  initialSelection={initialLanguage}
                  disabled={disabledLanguageSelector}
                  triggerOnChangeForInitialSelection={true}
                  allowUnset={false}
                  data-testid="LanguageSelector"
                />
              </InputGroup>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CountryMarketSelector;
